import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { PhysicsTable } from "../../../entities/physics_table.js";

const UP = new THREE.Vector3( 0, 1, 0 );
const TMP_Q_DELTA = new THREE.Quaternion();
const TMP_Q_BODY = new THREE.Quaternion();
const TMP_V = new THREE.Vector3();

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const TWO_PI = Math.PI * 2;
const DEFAULT_ANIMATE_SPEED = 180 * DEG_TO_RAD;

export class FieldRotator
{
  private angle = 0;
  private spin_speed = 0;
  private animation: { target: number; speed: number } | null = null;

  constructor(
    private readonly table: PhysicsTable,
    private readonly get_bodies: () => Iterable<RAPIER.RigidBody>
  )
  {
  }

  public isActive(): boolean
  {
    return this.spin_speed !== 0 || this.animation !== null;
  }

  public setAngle( deg: number, animate?: { deg_per_sec?: number } ): void
  {
    this.spin_speed = 0;
    const target = deg * DEG_TO_RAD;

    if ( !animate )
    {
      this.animation = null;
      this.applyDelta( this.shortestDeltaTo( target ) );
      return;
    }

    this.animation = {
      target: this.angle + this.shortestDeltaTo( target ),
      speed: Math.abs( animate.deg_per_sec ?? 0 ) * DEG_TO_RAD || DEFAULT_ANIMATE_SPEED
    };
  }

  public startSpin( deg_per_sec: number ): void
  {
    this.animation = null;
    this.spin_speed = deg_per_sec * DEG_TO_RAD;
  }

  public stopSpin(): void
  {
    this.spin_speed = 0;
  }

  public getAngleDeg(): number
  {
    const deg = ( this.angle * RAD_TO_DEG ) % 360;

    return deg < 0 ? deg + 360 : deg;
  }

  public step( dt: number ): void
  {
    if ( this.spin_speed !== 0 )
    {
      this.applyDelta( this.spin_speed * dt );
      return;
    }

    if ( this.animation )
    {
      const remaining = this.animation.target - this.angle;
      const max_step = this.animation.speed * dt;
      const delta = THREE.MathUtils.clamp( remaining, -max_step, max_step );

      if ( Math.abs( remaining ) <= max_step )
        this.animation = null;

      this.applyDelta( delta );
    }
  }

  private shortestDeltaTo( target_rad: number ): number
  {
    let delta = ( target_rad - this.angle ) % TWO_PI;

    if ( delta > Math.PI )
      delta -= TWO_PI;
    else if ( delta < -Math.PI )
      delta += TWO_PI;

    return delta;
  }

  private applyDelta( delta: number ): void
  {
    if ( delta === 0 )
      return;

    this.angle += delta;

    TMP_Q_DELTA.setFromAxisAngle( UP, delta );
    const pivot = this.table.getPivot();

    for ( const body of this.get_bodies() )
    {
      // Entities may remove their body from the world before leaving the
      // entity list (e.g. a broken watermelon while its slices fade).
      if ( !body.isValid() )
        continue;

      const p = body.translation();

      if ( this.table.isOutside( p ) )
        continue;

      TMP_V.set( p.x - pivot.x, 0, p.z - pivot.z ).applyQuaternion( TMP_Q_DELTA );
      body.setTranslation(
        { x: pivot.x + TMP_V.x, y: p.y, z: pivot.z + TMP_V.z },
        false
      );

      const r = body.rotation();
      TMP_Q_BODY.set( r.x, r.y, r.z, r.w ).premultiply( TMP_Q_DELTA );
      body.setRotation(
        { x: TMP_Q_BODY.x, y: TMP_Q_BODY.y, z: TMP_Q_BODY.z, w: TMP_Q_BODY.w },
        false
      );

      if ( !body.isSleeping() )
      {
        const lv = body.linvel();
        TMP_V.set( lv.x, lv.y, lv.z ).applyQuaternion( TMP_Q_DELTA );
        body.setLinvel( { x: TMP_V.x, y: TMP_V.y, z: TMP_V.z }, false );

        const av = body.angvel();
        TMP_V.set( av.x, av.y, av.z ).applyQuaternion( TMP_Q_DELTA );
        body.setAngvel( { x: TMP_V.x, y: TMP_V.y, z: TMP_V.z }, false );
      }
    }

    this.table.setFieldAngle( this.angle );
  }
}
